/**
 * USD summary for the transfers list header (J5).
 *
 * Money is summed in **integer cents**, never as floats: `0.1 + 0.2` in
 * binary floating point is `0.30000000000000004`, which formats as a total
 * that is off by a fraction of a cent. Parsing to cents and summing integers
 * is exact for every amount the gateway can send.
 *
 * A row whose USD value is absent (the recorded `TransferView` projection has
 * no valuation field yet) or unparseable is **excluded** from the total and
 * counted, so the line can say so. It is never treated as zero: an unpriced
 * transfer is not a transfer worth nothing.
 */

import { UNKNOWN_VALUE_LABEL, usdPrecise } from "@/features/valuation/valuation";

/** The only field this module reads from a transfer row. */
export interface TransferUsdRow {
  amountUsd?: string | null;
}

export interface TransferUsdSummary {
  /** Visible (filtered) rows the summary was computed from. */
  count: number;
  /** Rows that carried a usable USD value and are included in `totalCents`. */
  pricedCount: number;
  /** Rows excluded from the total because their USD value is unknown. */
  excludedCount: number;
  /** Exact total of the priced rows, in cents. */
  totalCents: number;
  /** Render-ready header line, e.g. `3 transfers, $1,250.00 total`. */
  label: string;
}

/** Placeholder for "no USD total is available", reusing the shared dash convention. */
const UNPRICED_TOTAL = `$${UNKNOWN_VALUE_LABEL}`;

/**
 * Parses a USD amount from the wire into integer cents.
 *
 * Accepts an already-numeric value, and strings with a currency symbol,
 * grouping separators and a leading approximation mark (`"≈ $125,000.00"`).
 * Returns `null` — meaning "unknown", not zero — for anything that is not a
 * cent-precise amount, including fractional cents, which are ambiguous.
 */
export function parseUsdToCents(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return Number.isInteger(value * 100) ? Math.round(value * 100) : null;
  }

  const cleaned = value.trim().replace(/[^0-9.-]/g, "");
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;

  const [, sign, whole, fraction = ""] = match;
  const cents = Number(whole) * 100 + Number(`${fraction}00`.slice(0, 2));
  return sign === "-" ? -cents : cents;
}

/**
 * Formats integer cents as a USD string. Reuses the shared `usdPrecise`
 * formatter; dividing integer cents by 100 is exact, so no drift is
 * introduced on the way to the formatter.
 */
export function formatUsdCents(cents: number): string {
  return usdPrecise.format(cents / 100);
}

/**
 * Summarises the visible transfers into a single header line:
 * `N transfers, $X total`, plus `(excludes N without a price)` when any row
 * was excluded. When nothing visible is priced the total renders as `$—`
 * with the same reason rather than as a fabricated `$0.00`.
 */
export function summarizeTransferUsd(rows: TransferUsdRow[]): TransferUsdSummary {
  let pricedCount = 0;
  let excludedCount = 0;
  let totalCents = 0;

  for (const row of rows) {
    const cents = parseUsdToCents(row.amountUsd);
    if (cents === null) {
      excludedCount += 1;
    } else {
      pricedCount += 1;
      totalCents += cents;
    }
  }

  const count = rows.length;
  const countLabel = `${count} transfer${count === 1 ? "" : "s"}`;
  const exclusionNote = excludedCount > 0 ? ` (excludes ${excludedCount} without a price)` : "";
  const totalLabel = pricedCount === 0 && excludedCount > 0 ? UNPRICED_TOTAL : formatUsdCents(totalCents);

  return {
    count,
    pricedCount,
    excludedCount,
    totalCents,
    label: `${countLabel}, ${totalLabel} total${exclusionNote}`,
  };
}
